import React, { useState, useEffect } from 'react'

// packages
import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'
import { FormattedMessage, injectIntl } from 'react-intl'

// components
import Button from '../../../../components/Button/Button'
import RadioButton from '../../../../components/RadioButton/RadioButton'
import CurrencyInput from '../../../../components/CurrencyInput/CurrencyInput'
import ToolTip from '../../../../components/Tooltip/Tooltip'

// styles
import Styles from './marketForm.module.css'
import ShimmerLine from '../../../../components/shimmer-components/ShimmerLine'

// icons
import { ReactComponent as FlagIcon } from '../../../../assets/icons/imported/finish.svg'
import { ReactComponent as MarketIcon } from '../../../../assets/icons/imported/market.svg'

// redux
import { _addOrder } from '../../redux/actions'
import { useSelector, useDispatch } from 'react-redux'
import { _getBalances } from '../../../../redux/actions'
import { setBalances } from '../../../../redux/slices/balancesSlice'
import { setSnackbarData, setSnackbarOpen } from '../../../snackbar/redux/snackbarSlice'

// other
import { formatNumber } from '../../../helpers/formatNumber'
import { estimateUnitValue } from '../../../helpers/estimateValueBase'
import axios from 'axios'

const sliderMarks = {
  0: '0%',
  25: '25%',
  50: '50%',
  75: '75%',
  100: '100%'
}

function MarketForm() {
  const dispatch = useDispatch()
  //user data
  const userData = useSelector(state => state.userData?.value)
  const balances = useSelector(state => state.balances.value)
  const loggedIn = useSelector(state => state.loggedIn.value)

  const marketsList = useSelector(state => state.marketList.value)
  const selectedMarket = useSelector((state) => state.selectedMarket.value)
  const reduxLimitPrice = useSelector(state => state.limitPrice.value)
  const [buyQuantity, setBuyQuantity] = useState('')
  const [sellQuantity, setSellQuantity] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  const [buyPrice, setBuyPrice] = useState('')
  const [toCurrency, setToCurrency] = useState(marketsList.find(el => el.id === selectedMarket?.id)?.quote_unit)
  const [fromCurrency, setFromCurrency] = useState(marketsList.find(el => el.id === selectedMarket?.id)?.base_unit)
  const [selectedRadioButton, setSelectedRadioButton] = useState('limit')
  const marketTickers = useSelector(state => state.marketTickers.value)
  const currencies = useSelector(state => state.currencies.value)
  const [buyLimitPrice, setBuyLimitPrice] = useState('')
  const [sellLimitPrice, setSellLimitPrice] = useState('')
  const [refreshBalance, setRefreshBalance] = useState(false)
  const [buySliderValue, setBuySliderValue] = useState(0)
  const [sellSliderValue, setSellSliderValue] = useState(0)
  const [buyTotalPrice, setBuyTotalPrice] = useState('')
  const [sellTotalPrice, setSellTotalPrice] = useState('')
  const [sellConvertedPrice, setSellConvertedPrice] = useState('')
  const [buyConvertedPrice, setBuyConvertedPrice] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [formError, setFormError] = useState(false)
  const [buyFee, setBuyFee] = useState(null)
  const [sellFee, setSellFee] = useState(null)
  const [buyFeeLoading, setBuyFeeLoading] = useState(false)
  const [sellFeeLoading, setSellFeeLoading] = useState(false)
  const [buyFeeCalculationTimer, setBuyFeeCalculationTimer] = useState(null)
  const [sellFeeCalculationTimer, setSellFeeCalculationTimer] = useState(null)

  const handleBuySliderChange = value => {
    setBuySliderValue(value)
    let currency
    if (selectedRadioButton === 'limit') {
      currency = balances.find(el => el.currency === fromCurrency)
      if (buyLimitPrice) {
        setBuyQuantity((currency.balance * value) / 100)
      }
    } else {
      currency = balances.find(el => el.currency === toCurrency)
      setBuyPrice((currency.balance * value) / 100)
    }
  }

  const handleSellSliderChange = value => {
    setSellSliderValue(value)
    let currency
    if (selectedRadioButton === 'limit') {
      currency = balances.find(el => el.currency === fromCurrency)
      if (sellLimitPrice) {
        setSellQuantity((currency.balance * value) / 100)
      }
    } else {
      currency = balances.find(el => el.currency === toCurrency)
      setSellPrice((currency.balance * value) / 100)
    }
  }
  // ----- RESPONSIVENESS STATE ---------
  // ------------------------------------
  const [modalStates, setModalStates] = useState({
    quickBuySellOpen: false,
    convertOpen: false
  })

  useEffect(() => {
    setSellLimitPrice(parseFloat(reduxLimitPrice.replace(/,/g, '')))
    setBuyLimitPrice(parseFloat(reduxLimitPrice.replace(/,/g, '')))
  }, [reduxLimitPrice])

  const [isTableCollapsed, setIsTableCollapsed] = useState(window.innerWidth < 940)
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 700)

  const handleResize = () => {
    setIsTableCollapsed(window.innerWidth < 900)
    setIsMobileView(window.innerWidth < 700)
  }

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])
  // ------------------------------------
  // ------------------------------------

  useEffect(() => {
    // console.log(currencies)
    setFromCurrency(marketsList.find(el => el.id === selectedMarket.id)?.base_unit)
    setToCurrency(marketsList.find(el => el.id === selectedMarket.id)?.quote_unit)
    setBuyConvertedPrice(
      selectedRadioButton === 'limit'
        ? buyTotalPrice / buyLimitPrice
        : estimateUnitValue(
          buyQuantity ? toCurrency : fromCurrency,
          buyQuantity ? fromCurrency : toCurrency,
          parseFloat(buyQuantity || buyPrice),
          currencies,
          marketsList,
          marketTickers
        )
    )
  }, [
    buyQuantity,
    selectedRadioButton,
    selectedMarket,
    buyPrice,
    currencies,
    marketTickers,
    buyTotalPrice,
    buyConvertedPrice
  ])

  const resetInputValues = () => {
    setSellPrice('')
    setBuyPrice('')
    setBuyQuantity('')
    setSellQuantity('')
    setBuyLimitPrice('')
    setSellLimitPrice('')
    setBuySliderValue(0)
    setSellSliderValue(0)
  }

  useEffect(() => {
    setBuySliderValue(0)
    setSellSliderValue(0)
    // console.log(userData)
    if (loggedIn) {
      _getBalances(
        res => {
          dispatch(setBalances(res))
        },
        () => { }
      )
    }
  }, [refreshBalance, selectedMarket])

  useEffect(() => {
    resetInputValues()
  }, [selectedMarket, selectedRadioButton])

  useEffect(() => {
    setFromCurrency(marketsList.find(el => el.id === selectedMarket.id)?.base_unit)
    setToCurrency(marketsList.find(el => el.id === selectedMarket.id)?.quote_unit)
    setSellConvertedPrice(
      selectedRadioButton === 'limit'
        ? sellTotalPrice / sellLimitPrice
        : estimateUnitValue(
          sellQuantity ? toCurrency : fromCurrency,
          sellQuantity ? fromCurrency : toCurrency,
          parseFloat(sellQuantity || sellPrice),
          currencies,
          marketsList,
          marketTickers
        )
    )
  }, [sellQuantity, marketTickers, selectedMarket, sellPrice, sellTotalPrice, sellConvertedPrice])

  // Manual triggers for fee calculation
  const handleBuyQuantityChange = event => {
    const newValue = event.target.value
    setBuyQuantity(newValue)
    setBuyPrice('')
    setBuyTotalPrice('')

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      setBuyFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      // Call with immediate value, not waiting for state update
      calculateBuyFeeWithValue(newValue, null, null)
    }
  }

  const handleSellQuantityChange = event => {
    const newValue = event.target.value
    setSellQuantity(newValue)
    setSellPrice('')
    setSellTotalPrice('')

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      setSellFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      // Call with immediate value, not waiting for state update
      calculateSellFeeWithValue(newValue, null, null)
    }
  }

  const handleBuyPriceChange = event => {
    const newValue = event.target.value
    setBuyPrice(newValue)
    setBuyQuantity('')

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      setBuyFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      // Call with immediate value, not waiting for state update
      calculateBuyFeeWithValue(null, newValue, null)
    }
  }

  const handleSellPriceChange = event => {
    const newValue = event.target.value
    setSellPrice(newValue)
    setSellQuantity('')

    // Reset fee to null if the input is empty
    if (!newValue || parseFloat(newValue) <= 0) {
      setSellFee(null)
      return
    }

    // Only calculate fee if there's a valid input - using the NEW value directly
    if (newValue && parseFloat(newValue) > 0) {
      // Call with immediate value, not waiting for state update
      calculateSellFeeWithValue(null, newValue, null)
    }
  }

  const handleBuyLimitPriceChange = event => {
    const newValue = event.target.value
    setBuyLimitPrice(newValue)

    // Reset fee if the limit price is deleted and it's needed for calculation
    if ((!newValue || parseFloat(newValue) <= 0) && selectedRadioButton === 'limit') {
      setBuyFee(null)
      return
    }

    // Only calculate fee if there's a valid input and we have a quantity
    if (newValue && parseFloat(newValue) > 0 && buyQuantity && parseFloat(buyQuantity) > 0) {
      // Call with immediate value, not waiting for state update
      calculateBuyFeeWithValue(buyQuantity, null, newValue)
    }
  }

  const handleSellLimitPriceChange = event => {
    const newValue = event.target.value
    setSellLimitPrice(newValue)

    // Reset fee if the limit price is deleted and it's needed for calculation
    if ((!newValue || parseFloat(newValue) <= 0) && selectedRadioButton === 'limit') {
      setSellFee(null)
      return
    }

    // Only calculate fee if there's a valid input and we have a quantity
    if (newValue && parseFloat(newValue) > 0 && sellQuantity && parseFloat(sellQuantity) > 0) {
      // Call with immediate value, not waiting for state update
      calculateSellFeeWithValue(sellQuantity, null, newValue)
    }
  }

  // New fee calculation functions that take direct values
  const calculateBuyFeeWithValue = async (directQuantity, directPrice, directLimitPrice) => {
    if (!selectedMarket?.id) return

    setBuyFeeLoading(true)
    try {
      // Use provided values if available, otherwise fall back to state
      const currentBuyQuantity = directQuantity !== null ? directQuantity : buyQuantity
      const currentBuyPrice = directPrice !== null ? directPrice : buyPrice
      const currentBuyLimitPrice = directLimitPrice !== null ? directLimitPrice : buyLimitPrice
      const currentBuyConvertedPrice = buyConvertedPrice
      const currentBuyTotalPrice = buyTotalPrice

      // Log all potential values to diagnose the issue
      console.log('Buy fee calculation debug (with direct values):')
      console.log('- buyQuantity:', currentBuyQuantity, 'type:', typeof currentBuyQuantity)
      console.log('- buyPrice:', currentBuyPrice, 'type:', typeof currentBuyPrice)
      console.log('- buyLimitPrice:', currentBuyLimitPrice, 'type:', typeof currentBuyLimitPrice)

      let price = 0
      // For limit orders, use limit price
      if (selectedRadioButton === 'limit') {
        price = parseFloat(currentBuyLimitPrice || 0)
      } else {
        price = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      }

      // Directly use amount from the right field based on order type
      let amount = 0
      if (selectedRadioButton === 'limit') {
        // For limit orders, use the quantity field
        amount = parseFloat(currentBuyQuantity || 0)
      } else {
        // For market orders, use the quantity field if available
        if (currentBuyQuantity && parseFloat(currentBuyQuantity) > 0) {
          amount = parseFloat(currentBuyQuantity)
        } else if (currentBuyConvertedPrice && parseFloat(currentBuyConvertedPrice) > 0) {
          amount = parseFloat(currentBuyConvertedPrice)
        } else if (currentBuyPrice && parseFloat(currentBuyPrice) > 0) {
          amount = parseFloat(currentBuyPrice) / price
        }
      }

      // Ensure we have positive values
      price = Math.max(0, price)
      amount = Math.max(0, amount)

      // Calculate total price (amount * price)
      const totalPrice = amount * price

      console.log('Final calculation values:')
      console.log('- Amount:', amount, 'Price:', price, 'Total:', totalPrice)

      // If total price is 0, don't make the API call
      if (totalPrice <= 0) {
        console.log('Skipping API call because totalPrice is 0')
        setBuyFee(null)
        return
      }

      const requestBody = {
        market: selectedMarket.id,
        ord_type: selectedRadioButton,
        price: totalPrice.toString()
      }

      console.log('Buy fee request:', requestBody)

      const response = await axios.post('/api/v2/trading/market/orders/estimate_fee', requestBody)
      setBuyFee(response.data)
    } catch (error) {
      console.error('Error fetching buy fee:', error)
    } finally {
      setBuyFeeLoading(false)
    }
  }

  const calculateSellFeeWithValue = async (directQuantity, directPrice, directLimitPrice) => {
    if (!selectedMarket?.id) return

    setSellFeeLoading(true)
    try {
      // Use provided values if available, otherwise fall back to state
      const currentSellQuantity = directQuantity !== null ? directQuantity : sellQuantity
      const currentSellPrice = directPrice !== null ? directPrice : sellPrice
      const currentSellLimitPrice = directLimitPrice !== null ? directLimitPrice : sellLimitPrice
      const currentSellConvertedPrice = sellConvertedPrice
      const currentSellTotalPrice = sellTotalPrice

      // Log all potential values to diagnose the issue
      console.log('Sell fee calculation debug (with direct values):')
      console.log('- sellQuantity:', currentSellQuantity, 'type:', typeof currentSellQuantity)
      console.log('- sellPrice:', currentSellPrice, 'type:', typeof currentSellPrice)
      console.log('- sellLimitPrice:', currentSellLimitPrice, 'type:', typeof currentSellLimitPrice)

      let price = 0
      // For limit orders, use limit price
      if (selectedRadioButton === 'limit') {
        price = parseFloat(currentSellLimitPrice || 0)
      } else {
        price = parseFloat(marketTickers[selectedMarket.id]?.last || 0)
      }

      // Directly use amount from the right field based on order type
      let amount = 0
      if (selectedRadioButton === 'limit') {
        // For limit orders, use the quantity field
        amount = parseFloat(currentSellQuantity || 0)
      } else {
        // For market orders, use the quantity field if available
        if (currentSellQuantity && parseFloat(currentSellQuantity) > 0) {
          amount = parseFloat(currentSellQuantity)
        } else if (currentSellConvertedPrice && parseFloat(currentSellConvertedPrice) > 0) {
          amount = parseFloat(currentSellConvertedPrice)
        } else if (currentSellPrice && parseFloat(currentSellPrice) > 0) {
          amount = parseFloat(currentSellPrice) / price
        }
      }

      // Ensure we have positive values
      price = Math.max(0, price)
      amount = Math.max(0, amount)

      // Calculate total price (amount * price)
      const totalPrice = amount * price

      console.log('Final calculation values:')
      console.log('- Amount:', amount, 'Price:', price, 'Total:', totalPrice)

      // If total price is 0, don't make the API call
      if (totalPrice <= 0) {
        console.log('Skipping API call because totalPrice is 0')
        setSellFee(null)
        return
      }

      const requestBody = {
        market: selectedMarket.id,
        ord_type: selectedRadioButton,
        price: totalPrice.toString()
      }

      console.log('Sell fee request:', requestBody)

      const response = await axios.post('/api/v2/trading/market/orders/estimate_fee', requestBody)
      setSellFee(response.data)
    } catch (error) {
      console.error('Error fetching sell fee:', error)
    } finally {
      setSellFeeLoading(false)
    }
  }

  // Update original functions to use new ones
  const calculateBuyFee = () => calculateBuyFeeWithValue(null, null, null)
  const calculateSellFee = () => calculateSellFeeWithValue(null, null, null)

  // Update fees when order type changes
  useEffect(() => {
    // Clear previous fees
    setBuyFee(null)
    setSellFee(null)

    // Only calculate if we have the necessary values
    if (buyQuantity && parseFloat(buyQuantity) > 0) {
      calculateBuyFee()
    }

    if (sellQuantity && parseFloat(sellQuantity) > 0) {
      calculateSellFee()
    }
  }, [selectedRadioButton, selectedMarket?.id])

  const handleSubmit = orderType => {
    setSubmitted(true)
    if (parseFloat(buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : '') < parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)) {
      // If there's a form error, prevent the API call and show the error
      console.log("Form submission prevented due to errors.")
      return
    }

    if (parseFloat(sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : '') < parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)) {
      // If there's a form error, prevent the API call and show the error
      console.log("Form submission prevented due to errors2.")
      return
    }

    console.log(sellQuantity)
    _addOrder(
      {
        market: selectedMarket.id,
        side: orderType,
        volume: orderType === 'buy' ? buyQuantity || buyConvertedPrice : sellQuantity || sellConvertedPrice,
        ...(selectedRadioButton === 'limit' && {
          price: orderType === 'buy' ? buyLimitPrice : sellLimitPrice
        }),
        ord_type: selectedRadioButton
      },
      data => {
        dispatch(
          setSnackbarData({
            alertMessage: 'Order set',
            buttonMessage: 'See order',
            callback: () => { },
            actionType: 'link', // link - close
            linkTo: '/account/orders/open',
            severity: 'success',
            position: 'topRight'
          })
        )
        dispatch(setSnackbarOpen(true))
        // dispatch(setSnackbarData({
        //   alertMessage: 'Information Added Successfully',
        //   severity: 'success',
        //   callback: () => { },
        //   position: 'topRight'
        // }))
        //dispatch(addOrder(data))
        resetInputValues()
        setRefreshBalance(!refreshBalance)
      },
      error => {
        dispatch(setSnackbarOpen(true))
        dispatch(
          setSnackbarData({
            alertMessage: error.errors[0].length ? <FormattedMessage id={error.errors[0]} /> : 'Unknown Error',
            severity: 'error',
            //buttonMessage: 'See order',
            callback: () => { },
            //actionType: 'link', // link - close
            //linkTo: '/account/orders/open',
            position: 'topRight'
          })
        )
      }
    )
  }

  // const handleFormValidation = () => {
  //   setSubmitted(true)

  //   const minAmount = parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)

  //   // Check for buy quantity or price and validate minimum amount
  //   if (
  //     (parseFloat(buyQuantity || buyConvertedPrice) < minAmount) ||
  //     (parseFloat(sellQuantity || sellConvertedPrice) < minAmount)
  //   ) {
  //     setFormError(true)  // Set the error flag to true if any error exists
  //     return true
  //   }

  //   setFormError(false)  // No error
  //   return false
  // }

  const handleBuyTotalPriceChange = event => {
    console.log(event.target.value)
    setBuyTotalPrice(event.target.value)
    setBuyQuantity('')
  }

  const handleSellTotalPriceChange = event => {
    setSellTotalPrice(event.target.value)
    setSellQuantity('')
  }

  const coinNamePrice = useSelector(state => state.selectedMarket.value?.name?.split('/')[1])
  return (
    <div className={Styles.card}>
      {/* radio buttons container */}
      <div className={Styles.radioButtonsContainer}>
        <RadioButton
          text={<FormattedMessage id='page.markets.trade.limit' />}
          icon={<FlagIcon />}
          isSelected={selectedRadioButton === 'limit'}
          onClick={() => {
            if (selectedRadioButton === 'limit') return
            setSelectedRadioButton('limit')
          }}
        />
        <RadioButton
          text={<FormattedMessage id='page.markets.trade.market' />}
          icon={<MarketIcon width={12} />}
          isSelected={selectedRadioButton === 'market'}
          onClick={() => {
            if (selectedRadioButton === 'market') return
            setSelectedRadioButton('market')
          }}
        />

        <ToolTip
          toolTipStyle={{
            margin: '0 0 auto auto'
          }}
          text={
            <>
              <div>
                Limit: Want to buy or sell at a specific price? A limit order lets you do that, but remember that it
                might not always go through.A limit order lets you do that, but remember that it might not always go
                through.
              </div>
              <div
                style={{
                  marginTop: 20
                }}
              >
                Market: Buying or selling now means you'll get a quick trade, taking crypto prices from the current
                market.
              </div>
            </>
          }
        />
      </div>

      {/* MAIN Buy/Sell container */}
      <div className={Styles.mainBuySellContainer}>
        {/* Buy Container */}
        <div className={Styles.buyContainer}>
          <span className={Styles.balanceAvailable}>
            {userData.email &&
              (balances ? (
                balances.find(el => el.currency === toCurrency)?.balance ? (
                  `Available balance: ${formatNumber(balances.find(el => el.currency === toCurrency)?.balance) +
                  ' ' +
                  balances.find(el => el.currency === toCurrency)?.currency.toUpperCase() ?? '0.00'
                  }`
                ) : (
                  `Available balance: 0.00 ${toCurrency?.toUpperCase()}`
                )
              ) : (
                <ShimmerLine height='10px' width='70px' />
              ))}
          </span>
          <CurrencyInput
            title={selectedRadioButton === 'limit' ? `Limit Price` : 'Price'}
            onChange={selectedRadioButton === 'limit' ? handleBuyLimitPriceChange : handleBuyPriceChange}
            type="number"
            onDropdownClick={() => { }}
            disabled={selectedRadioButton === 'market'}
            placeholder={selectedRadioButton === 'limit' ? '0.00' : buyConvertedPrice ? buyConvertedPrice : '0.00'}
            value={selectedRadioButton === 'limit' ? buyLimitPrice : buyPrice}
            containerStyle={{
              marginTop: 15
            }}
            currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.quote_url}
            coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[1])}
          />

          <CurrencyInput
            title={`Amount`}
            onChange={handleBuyQuantityChange}
            type='number'
            onDropdownClick={() => { }}
            placeholder='0.00'
            value={buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : ''}
            containerStyle={{
              marginTop: 15
            }}
            error={submitted && parseFloat(buyQuantity || buyConvertedPrice ? buyQuantity || buyConvertedPrice : '') < parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)
              ? { message: `Minimum deposit amount is ${marketsList.find(el => el.id === selectedMarket.id)?.min_amount}` }
              : undefined}
            coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[0])}
            currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.icon_url}
          />
          {/* slider here */}
          {userData.email && (
            <Slider
              min={0}
              max={100}
              marks={sliderMarks}
              value={buySliderValue}
              onChange={handleBuySliderChange}
              trackStyle={[{ backgroundColor: 'var(--primary-color)' }]}
              railStyle={{ backgroundColor: 'var(--gray-color)' }}
              handleStyle={{
                height: 12,
                width: 12,
                marginTop: -3.5,
                backgroundColor: 'white',
                border: `2px solid var(--primary-color)`,
                borderRadius: '50%',
                boxShadow: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              dotStyle={{
                backgroundColor: 'var(--gray-color)',
                borderColor: 'var(--gray-color)'
              }}
              activeDotStyle={{
                backgroundColor: 'var(--primary-color)',
                borderColor: 'var(--primary-color)'
              }}
              style={{ margin: '30px auto', width: '90%' }}
            />
          )}
          {selectedRadioButton === 'limit' && (
            <CurrencyInput
              title={`Total`}
              // currencyIcon=""
              onChange={handleBuyTotalPriceChange}
              type='number'
              // disabled={true}
              onDropdownClick={() => { }}
              placeholder='0.00'
              value={
                buyTotalPrice
                  ? buyTotalPrice
                  : buyPrice || buyLimitPrice * buyQuantity
                    ? buyPrice || buyLimitPrice * buyQuantity
                    : ''
              }
              containerStyle={{
                marginTop: 15
              }}
              coinName={coinNamePrice}
            />
          )}
          <div className={"mt-3"}>
            {buyFeeLoading ? (
              <div className={Styles.feesInfoRow}>
                <span>Fee:</span>
                <ShimmerLine width='80px' height='18px' />
              </div>
            ) : (
              <div className={Styles.feesInfoRow}>
                <span>Fee:</span>
                <span>
                  {buyFee ? `${formatNumber(buyFee.fee_amount)} ${selectedMarket?.quote_unit?.toUpperCase()} (${formatNumber(buyFee.fee_percentage)}%)` : `0 ${selectedMarket?.quote_unit?.toUpperCase()} (0%)`}
                </span>
              </div>
            )}
          </div>
          {/* buy button */}
          {userData.email ? (

            <Button
              text={`Buy ${marketsList.find(el => el.id === selectedMarket.id)?.base_unit.toUpperCase()}`}
              style={{
                marginTop: 0,
                backgroundColor: '#10B981',
                color: 'white'
              }}
              onClick={e => {
                handleSubmit('buy')
              }}
              className={Styles.buyButton}
            />
          ) : (
            <Button type='loggedOut' />
          )}
        </div>

        {/* Sell Container */}
        <div className={Styles.sellContainer}>
          <span className={Styles.balanceAvailable}>
            {userData.email &&
              (balances ? (
                balances.find(el => el.currency === fromCurrency)?.balance ? (
                  `Available balance: ${formatNumber(balances.find(el => el.currency === fromCurrency)?.balance) +
                  ' ' +
                  balances.find(el => el.currency === fromCurrency)?.currency.toUpperCase() ?? '0.00'
                  }`
                ) : (
                  `Available balance: 0.00 ${fromCurrency?.toUpperCase()}`
                )
              ) : (
                <ShimmerLine height='10px' width='70px' />
              ))}
          </span>
          <CurrencyInput
            title={selectedRadioButton === 'limit' ? `Limit Price` : 'Price'}
            onChange={selectedRadioButton === 'limit' ? handleSellLimitPriceChange : handleSellPriceChange}
            disabled={selectedRadioButton === 'market'}
            type='number'
            onDropdownClick={() => { }}
            placeholder={selectedRadioButton === 'limit' ? '0.00' : sellConvertedPrice ? sellConvertedPrice : '0.00'}
            value={selectedRadioButton === 'limit' ? sellLimitPrice : sellPrice}
            containerStyle={{
              marginTop: 15
            }}
            currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.quote_url}
            coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[1])}
          />
          <CurrencyInput
            title={`Amount`}
            onChange={handleSellQuantityChange}
            type='number'
            onDropdownClick={() => { }}
            placeholder='0.00'
            value={sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : ''}
            containerStyle={{
              marginTop: 15
            }}
            error={submitted && parseFloat(sellQuantity || sellConvertedPrice ? sellQuantity || sellConvertedPrice : '') < parseFloat(marketsList.find(el => el.id === selectedMarket.id)?.min_amount)
              ? { message: `Minimum deposit amount is ${marketsList.find(el => el.id === selectedMarket.id)?.min_amount}` }
              : undefined}
            coinName={useSelector(state => state.selectedMarket.value?.name?.split('/')[0])}
            currencyImg={marketsList.find(el => el.id === selectedMarket.id)?.icon_url}
          />

          {/* slider here */}
          {userData.email && (
            <Slider
              min={0}
              max={100}
              marks={sliderMarks}
              value={sellSliderValue}
              onChange={handleSellSliderChange}
              trackStyle={[{ backgroundColor: 'var(--primary-color)' }]}
              railStyle={{ backgroundColor: 'var(--gray-color)' }}
              handleStyle={{
                height: 12,
                width: 12,
                marginTop: -3.5,
                backgroundColor: 'white',
                border: `2px solid var(--primary-color)`,
                borderRadius: '50%',
                boxShadow: 'none',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
              dotStyle={{
                backgroundColor: 'var(--gray-color)',
                borderColor: 'var(--gray-color)'
              }}
              activeDotStyle={{
                backgroundColor: 'var(--primary-color)',
                borderColor: 'var(--primary-color)'
              }}
              style={{ margin: '30px auto', width: '90%' }}
            />
          )}
          {selectedRadioButton === 'limit' && (
            <CurrencyInput
              title={`Total`}
              currencyIcon=''
              onChange={handleSellTotalPriceChange}
              type='number'
              // disabled={true}
              onDropdownClick={() => { }}
              placeholder='0.00'
              value={
                sellTotalPrice
                  ? sellTotalPrice
                  : sellPrice || sellLimitPrice * sellQuantity
                    ? sellPrice || sellLimitPrice * sellQuantity
                    : ''
              }
              containerStyle={{
                marginTop: 15
              }}
              coinName={coinNamePrice}
            />
          )}
          <div className={"mt-3"}>
            {sellFeeLoading ? (
              <div className={Styles.feesInfoRow}>
                <span>Fee:</span>
                <ShimmerLine width='80px' height='18px' />
              </div>
            ) : (
              <div className={Styles.feesInfoRow}>
                <span>Fee:</span>
                <span>
                  {sellFee ? `${formatNumber(sellFee.fee_amount)} ${selectedMarket?.quote_unit?.toUpperCase()} (${formatNumber(sellFee.fee_percentage)}%)` : `0 ${selectedMarket?.quote_unit?.toUpperCase()} (0%)`}
                </span>
              </div>
            )}
          </div>
          {/* sell button */}
          {userData.email ? (
            <Button
              text={`Sell ${marketsList.find(el => el.id === selectedMarket.id)?.base_unit.toUpperCase()}`}
              style={{
                marginTop: 0,
                backgroundColor: 'rgba(240,68,68,255)',
                color: 'white'
              }}
              onClick={e => {
                handleSubmit('sell')
              }}
            />
          ) : (
            <Button type='loggedOut' />
          )}
        </div>
      </div>
    </div>
  )
}

export default injectIntl(MarketForm)
